<template>
    <div class="user-details-window">
        <div class="user-details-header">
            <img :src="member.picture" alt="User Avatar" class="details-avatar" />

            <div class="details-text">
                <h3>{{ member.name }}</h3>
                <p>{{ member.email }}</p>
            </div>
        </div>

        <v-divider class="section-divider" />

        <div class="user-details-body">
            <div class="info-card">
                <div class="info-card-header">
                    <div>
                        <div class="section-label">Role</div>
                        <div class="current-role">
                            {{ getUserTitleFromRoleName(member.role?.name) }}
                        </div>
                    </div>

                    <v-btn v-if="!isChangingRole && !isProtectedMember" color="primary" rounded variant="flat"
                        @click="displayRoles">
                        Change Role
                    </v-btn>
                </div>

                <transition name="fade-slide">
                    <div v-if="isChangingRole" class="role-editor">
                        <v-select v-model="newRoleSelectedId" :items="availableRoles" item-title="name" item-value="id"
                            label="Select Role" variant="outlined" density="comfortable" hide-details="auto"
                            class="role-select" />

                        <div v-if="selectedRole" class="role-description">
                            {{ selectedRole.description }}
                        </div>

                        <div class="action-buttons">
                            <v-btn variant="text" rounded @click="cancelRoleChange">
                                Cancel
                            </v-btn>

                            <v-btn color="primary" rounded :disabled="!newRoleSelectedId"
                                @click="updateMemberRoleAsync">
                                Save
                            </v-btn>
                        </div>
                    </div>
                </transition>
            </div>
        </div>

        <v-divider class="section-divider" />

        <div class="user-details-actions">
            <v-btn color="error" rounded variant="flat" @click="deleteMemberAsync">
                Delete
            </v-btn>
        </div>
    </div>
</template>

<script setup>
import { computed, defineEmits, defineOptions, defineProps, onMounted, ref } from 'vue';
import AuthZeroService from '@/services/AuthZeroService';
import { useOrganizationStore } from '@/store/organizationStore';
import { useConfirm } from '@/composable/useConfirm';
import { getUserTitleFromRoleName } from '@/utils/AuthRolesMap';
import { ProtectedRoles } from '@/constants/roles';

defineOptions({
    name: 'AuthMemberListItem'
});

const props = defineProps({
    member: {
        type: Object,
        required: true
    }
});

const emits = defineEmits([
    'onMemberDeleted',
    'onMemberModified'
]);

const organizationStore = useOrganizationStore();
const { confirm } = useConfirm();

const availableRoles = ref([]);
const isChangingRole = ref(false);
const newRoleSelectedId = ref(null);

const isProtectedMember = computed(() => {
    return ProtectedRoles.includes(props.member.role?.name);
});

const selectedRole = computed(() => {
    return availableRoles.value.find(r => r.id === newRoleSelectedId.value);
});

onMounted(async () => {
    availableRoles.value = await AuthZeroService.getAssignableRolesAsync();
});

const displayRoles = () => {
    isChangingRole.value = true;
};

const cancelRoleChange = () => {
    isChangingRole.value = false;
    newRoleSelectedId.value = null;
};

const updateMemberRoleAsync = async () => {
    try {
        await AuthZeroService.assignRoles(
            organizationStore.authZeroOrgId,
            props.member.userId,
            props.member.role?.id,
            newRoleSelectedId.value
        );

        emits('onMemberModified');
        isChangingRole.value = false;
        newRoleSelectedId.value = null;
    } catch (err) {
        console.error('Failed to update member role', err);
    }
};

const deleteMemberAsync = async () => {
    const confirmed = await confirm({
        title: 'Remove member?',
        message: `${props.member.name} will lose access to this organization.`,
        confirmText: 'Remove',
        danger: true,
        icon: 'mdi-account-remove-outline',
    });
    if (!confirmed) return;

    try {
        await AuthZeroService.deleteUser(organizationStore.authZeroOrgId, props.member.userId);
        emits('onMemberDeleted');
    } catch (err) {
        console.error('Failed to delete member', err);
    }
};

</script>

<style scoped>
.user-details-window {
    padding: 20px;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(8px);
}

.user-details-header {
    display: flex;
    align-items: center;
    gap: 16px;
}

.details-avatar {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.14);
    flex-shrink: 0;
}

.details-text {
    min-width: 0;
}

.details-text h3 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1.2;
}

.details-text p {
    margin: 6px 0 0;
    color: rgba(255, 255, 255, 0.68);
    font-size: 0.95rem;
    word-break: break-word;
}

.section-divider {
    margin: 20px 0;
    opacity: 0.7;
}

.user-details-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.info-card {
    padding: 16px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.035);
    border: 1px solid rgba(255, 255, 255, 0.06);
}

.info-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
}

.section-label {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(255, 255, 255, 0.55);
    margin-bottom: 6px;
}

.current-role {
    font-size: 1rem;
    font-weight: 600;
    color: white;
}

.role-editor {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
}

.role-select {
    width: 100%;
}

.role-description {
    width: 100%;
    padding: 12px 14px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.82);
    font-size: 0.95rem;
    line-height: 1.5;
    white-space: normal;
    word-break: break-word;
    white-space: normal;
    max-width: 60ch;
    margin: 0 auto;
}

.action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    width: 100%;
}

.user-details-actions {
    display: flex;
    justify-content: flex-end;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.2s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

@media (max-width: 600px) {
    .user-details-window {
        padding: 16px;
    }

    .info-card-header {
        flex-direction: column;
        align-items: stretch;
    }

    .action-buttons {
        justify-content: stretch;
    }

    .action-buttons :deep(.v-btn) {
        flex: 1;
    }
}
</style>